import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const services = [
    "Building Maintenance",
    "Temporary Staffing",
    "Parkade Sweeping",
    "Pressure Washing",
    "Drywall & Painting",
    "Handyman Service"
  ];

  const moreServices = [
    "Electrical Services",
    "Concrete Repair",
    "HVAC Filters",
    "Woodwork",
    "Junk Removal",
    "Window Cleaning"
  ];

  const quickLinks = [
    { label: "Home", href: "/homepage" },
    { label: "About Us", href: "/about-us" },
    { label: "Services", href: "/services" },
    { label: "Contact", href: "/contact" }
  ];

  return (
    <footer className="bg-dark-bg border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link href="/homepage" className="inline-flex items-center space-x-3 mb-6 group">
              <div className="relative w-12 h-12 flex-shrink-0">
                <AppImage 
                  src="/assets/images/os_logo_banner.jpg" 
                  alt="OS Property Services"
                  width={48}
                  height={48}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-heading text-lg font-bold text-white leading-tight">
                  OS Property Services
                </span>
                <span className="text-xs text-slate-400">
                  Commercial Maintenance
                </span>
              </div>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Your trusted partner for comprehensive commercial property maintenance across the Lower Mainland.
            </p>
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-3">
              <Icon name="MapPinIcon" size={18} variant="outline" className="text-primary-light flex-shrink-0" />
              <span>Lower Mainland, BC</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Icon name="ClockIcon" size={18} variant="outline" className="text-primary-light flex-shrink-0" />
              <span>24/7 On-Call Availability</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading text-white font-bold text-lg mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-slate-400 hover:text-primary-light transition-colors duration-200 text-sm flex items-center gap-2 group"
                  >
                    <Icon name="ArrowRightIcon" size={14} variant="outline" className="opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all duration-200" />
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Column 1 */}
          <div>
            <h3 className="font-heading text-white font-bold text-lg mb-6">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <Link
                    href="/services"
                    className="text-slate-400 hover:text-primary-light transition-colors duration-200 text-sm flex items-center gap-2 group"
                  >
                    <Icon name="CheckIcon" size={14} variant="solid" className="text-primary/50 flex-shrink-0" />
                    <span>{service}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Column 2 */}
          <div>
            <h3 className="font-heading text-white font-bold text-lg mb-6">More Services</h3>
            <ul className="space-y-3">
              {moreServices.map((service) => (
                <li key={service}>
                  <Link
                    href="/services"
                    className="text-slate-400 hover:text-primary-light transition-colors duration-200 text-sm flex items-center gap-2 group"
                  >
                    <Icon name="CheckIcon" size={14} variant="solid" className="text-primary/50 flex-shrink-0" />
                    <span>{service}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm text-center md:text-left">
              © {currentYear} OS Property Services. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link
                href="/privacy-policy"
                className="text-slate-400 hover:text-primary-light transition-colors duration-200 text-sm"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms-of-service"
                className="text-slate-400 hover:text-primary-light transition-colors duration-200 text-sm"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
